/**
 * Meta / Facebook Pixel helper.
 *
 * Loads `fbevents.js` once (globally) and initialises one or more Pixel IDs.
 * Meta's SDK natively supports multiple init calls — each additional pixel
 * id gets its own bucket. We use `fbq('trackSingle', pixelId, event, data)`
 * so an event only fires for the pixel that triggered it, keeping per-lender
 * attribution clean when a visitor bounces between offers in one session.
 *
 * Public API:
 *   • initMetaPixel(pixelId)   — load the SDK (once), init this pixel,
 *                                and fire a PageView on it. Safe to call
 *                                repeatedly with the same id; guarded so
 *                                we init each id at most once.
 *   • trackLead(pixelId, data) — fire a "Lead" event scoped to that pixel.
 *                                Used when the user submits their phone.
 */

let sdkLoaded = false;
const initedPixels = new Set();

/**
 * Meta Pixel bootstrap snippet (verbatim from Meta's docs, wrapped in a
 * function so we can call it lazily instead of on every page load).
 */
function loadFbSdkOnce() {
  if (sdkLoaded || typeof window === "undefined") return;
  sdkLoaded = true;
  /* eslint-disable */
  (function (f, b, e, v, n, t, s) {
    if (f.fbq) return;
    n = f.fbq = function () {
      n.callMethod
        ? n.callMethod.apply(n, arguments)
        : n.queue.push(arguments);
    };
    if (!f._fbq) f._fbq = n;
    n.push = n;
    n.loaded = !0;
    n.version = "2.0";
    n.queue = [];
    t = b.createElement(e);
    t.async = !0;
    t.src = v;
    s = b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t, s);
  })(
    window,
    document,
    "script",
    "https://connect.facebook.net/en_US/fbevents.js"
  );
  /* eslint-enable */
}

/**
 * Initialise a specific pixel and fire its PageView. Called when the
 * phone-capture dialog opens for a product that has a `meta_pixel_id`.
 */
export function initMetaPixel(pixelId) {
  if (!pixelId) return;
  const id = String(pixelId).trim();
  if (!id) return;
  loadFbSdkOnce();
  if (initedPixels.has(id)) {
    // Already inited this pixel earlier — just fire another PageView so
    // Meta counts each dialog-open as a discrete engagement.
    if (typeof window.fbq === "function") {
      window.fbq("trackSingle", id, "PageView");
    }
    return;
  }
  initedPixels.add(id);
  if (typeof window.fbq === "function") {
    window.fbq("init", id);
    window.fbq("trackSingle", id, "PageView");
  }
}

/**
 * Fire a Meta Pixel Lead event scoped to a specific pixel id. Called after
 * the user submits their phone / KYC and we've successfully sent the lead
 * (or handed off to the partner's page).
 */
export function trackLead(pixelId, data = {}) {
  if (!pixelId) return;
  const id = String(pixelId).trim();
  if (!id || typeof window === "undefined" || typeof window.fbq !== "function") return;
  window.fbq("trackSingle", id, "Lead", data);
}
